import global from '@app/filters/global';
import { Map } from '@app/services/export/ppt/classes/Map';
import { TextWithDimensions } from '@app/services/export/ppt/classes/TextWithDimensions';
import PowerPointPresentation from '@app/services/export/ppt/PowerPointPresentation';
import PowerPointSlideProcessor from '@app/services/export/ppt/slides/PowerPointSlideProcessor';
import { gridColWidth } from '@app/services/export/ppt/utils/gridUtils';
import {
	createBadgeForSlide,
	createImageForSlide,
	createLogoForSlide,
	getBadgeWidthFromText
} from '@app/services/export/ppt/utils/layoutUtils';
import {
	colors,
	defaultViewPortPadding,
	elementSizes,
	presentationDimensions
} from '@app/services/export/ppt/variables/constants';
import { copy, copySmall, h3 } from '@app/services/export/ppt/variables/typography';
import CloudinaryTransform from '@app/transforms/CloudinaryTransform';
import IImage from '@app/types/data/base/IImage';
import IOfferSlideData from '@app/types/data/slides/IOfferSlideData';
import PptxGenJS from 'pptxgenjs';

export interface IPptOfferSlideOptions {
	imageWidth: number;
	textY: number;
	omitImage?: boolean;
	debugContentRect?: boolean;
}

export interface IPptOfferSlideCreateOptions {
	onBeforeCreate?: (slide: PptxGenJS.Slide, data: IOfferSlideData) => void;
	omitTopBar?: boolean;
}

export default abstract class AbstractOfferSlideProcessor<
	D extends IOfferSlideData
> extends PowerPointSlideProcessor<D> {
	protected readonly badgeY = 0.66;

	protected readonly copyMargin = 0.08;

	protected masterName?: string;

	protected options: IPptOfferSlideOptions = {
		imageWidth: defaultViewPortPadding.horizontal + gridColWidth(6),
		textY: 0.7
	};

	protected contentX: number;

	protected headlineWithDimensions?: TextWithDimensions;

	protected copyWithDimensions?: TextWithDimensions;

	protected constructor(slideData: D, presentation: PowerPointPresentation, options: Partial<IPptOfferSlideOptions>) {
		super(slideData, presentation);
		this.options = { ...this.options, ...options };
		this.contentX = this.options.imageWidth + gridColWidth(0.5);
		this.initLayout();
	}

	private initLayout(): void {
		if (this.slideData.badgeText) {
			this.options.textY = 1.15;
		}

		this.headlineWithDimensions = undefined;
		this.copyWithDimensions = undefined;

		if (this.slideData.headline) {
			this.headlineWithDimensions = new TextWithDimensions({
				text: this.slideData.headline,
				textOptions: {
					...h3,
					color: colors.blue
				},
				maxWidth: presentationDimensions.w - this.contentX - defaultViewPortPadding.horizontal
			});
		}

		if (this.slideData.copy) {
			this.copyWithDimensions = new TextWithDimensions({
				text: this.slideData.copy,
				useRichText: true,
				textOptions: { color: colors.blue, ...copy },
				maxWidth: presentationDimensions.w - this.contentX - defaultViewPortPadding.horizontal
			});
		}
	}

	protected createOfferSlideBase(options?: IPptOfferSlideCreateOptions): PptxGenJS.Slide {
		const slide = this.presentation.addSlide({ masterName: this.masterName });
		let map: Map | null = null;

		if (options && options.onBeforeCreate) {
			options.onBeforeCreate(slide, this.slideData);
		}

		// map
		if (!this.options.omitImage) {
			if (this.slideData.image && this.slideData.map) {
				map = new Map(
					slide,
					{
						image: this.slideData.image,
						map: this.slideData.map,
						labels: this.slideData.mapLabels,
						visibleSvgGroup: this.slideData.visibleSvgGroup,
						showCover: true,
						ignoreImage: !!this.masterName
					},
					{
						x: 0,
						y: 0,
						w: this.options.imageWidth,
						h: presentationDimensions.h
					}
				);

				map.create();
			} else if (this.slideData.image) {
				// image only
				createImageForSlide(slide, this.slideData.image, {
					x: 0,
					y: 0,
					destinationWidth: this.options.imageWidth,
					destinationHeight: presentationDimensions.h,
					type: 'cover'
				});
			}
		}

		// texts

		if (this.headlineWithDimensions) {
			this.headlineWithDimensions.createForSlide(slide, { x: this.contentX, y: this.options.textY });
		}

		if (this.copyWithDimensions) {
			this.copyWithDimensions.createForSlide(slide, {
				x: this.contentX,
				y: this.options.textY + this.getHeadlineHeight() + this.copyMargin
			});
		}

		// badge

		if (this.slideData.badgeText) {
			createBadgeForSlide(slide, this.slideData.badgeText, {
				x: this.options.imageWidth - getBadgeWidthFromText(this.slideData.badgeText) / 2,
				y: this.badgeY
			});
		}

		// header & footer

		if (!options || !options.omitTopBar) {
			this.createTopBar(slide);
		}

		this.createFootnote(slide);
		createLogoForSlide(slide);

		if (this.options.debugContentRect) {
			slide.addShape('rect', {
				y: this.getOfferSlideContentY(),
				x: this.getOfferSlideContentX(),
				w: this.getOfferSlideContentW(),
				h: this.getOfferSlideContentH(),
				fill: { color: colors.lightYellow }
			});
		}

		return slide;
	}

	/**
	 * Adjusts the width/height of the image object based on the real cloudinary output dimensions.
	 *
	 * @param image
	 * @protected
	 */
	protected async adjustImageDimensions(image: IImage): Promise<void> {
		const imageOutputSize = await CloudinaryTransform.getOutputSize(image);
		// eslint-disable-next-line no-param-reassign
		image.width = imageOutputSize.width;
		// eslint-disable-next-line no-param-reassign
		image.height = imageOutputSize.height;
	}

	private getOfferSlideContentY(): number {
		const copyMargin = this.slideData.copy ? this.copyMargin : 0;
		const bufferMargin = 0.25;
		return this.options.textY + this.getHeadlineHeight() + this.getCopyHeight() + copyMargin + bufferMargin;
	}

	private getOfferSlideContentX(): number {
		return this.options.imageWidth + gridColWidth(0.5);
	}

	private getOfferSlideContentW(): number {
		return presentationDimensions.w - this.getOfferSlideContentX() - defaultViewPortPadding.horizontal;
	}

	private getOfferSlideContentH(): number {
		return presentationDimensions.h - this.getOfferSlideContentY() - elementSizes.footnoteHeight;
	}

	private getHeadlineHeight(): number {
		return this.headlineWithDimensions ? this.headlineWithDimensions.getDimensions().h : 0;
	}

	private getCopyHeight(): number {
		return this.copyWithDimensions ? this.copyWithDimensions.getDimensions().h : 0;
	}

	public getAvailableSpaceRectangle(): Record<'x' | 'y' | 'w' | 'h', number> {
		return {
			x: this.getOfferSlideContentX(),
			y: this.getOfferSlideContentY(),
			w: this.getOfferSlideContentW(),
			h: this.getOfferSlideContentH()
		};
	}

	public updateSlideData(data: Partial<IOfferSlideData>, options?: Partial<IPptOfferSlideOptions>): void {
		this.slideData = { ...this.slideData, ...data };

		if (options) {
			this.options = { ...this.options, ...options };
		}
		this.initLayout();
	}

	private createTopBar = (slide: PptxGenJS.Slide): void => {
		const textArray: string[] = [];
		const h = elementSizes.metaHeaderHeight;
		const w = presentationDimensions.w - this.options.imageWidth;
		const x = this.options.imageWidth;
		const y = 0;

		const operatingTimes = this.mapOperatingTimes(this.slideData.operatingTimes);

		if (operatingTimes) {
			textArray.push(operatingTimes);
		}

		if (this.slideData.offerValidity) {
			textArray.push(this.slideData.offerValidity);
		}

		if (this.slideData.priceValidity) {
			textArray.push(this.slideData.priceValidity);
		}

		if (this.slideData.elevationMin && this.slideData.elevationMax) {
			textArray.push(
				`${this.i18n.elevation}: ${global.formatElevationRange({
					elevationMin: Number(this.slideData.elevationMin),
					elevationMax: Number(this.slideData.elevationMax)
				})}`
			);
		} else if (this.slideData.elevation) {
			textArray.push(`${this.i18n.elevation}: ${global.formatElevation(Number(this.slideData.elevation))}`);
		}

		if (!textArray.length) {
			return;
		}

		// text
		slide.addText(textArray.join('  ●  '), {
			...copySmall,
			x: x + gridColWidth(0.5),
			y,
			w: w - defaultViewPortPadding.horizontal - gridColWidth(0.5),
			h,
			align: 'left',
			valign: 'middle',
			margin: 0,
			color: colors.blue
		});

		// divider
		slide.addShape('line', {
			line: {
				color: colors.lightGray,
				width: 1
			},
			y: h,
			x,
			w,
			h: 0
		});
	};

	private createFootnote = (slide: PptxGenJS.Slide): void => {
		if (!this.slideData.footnote) {
			return;
		}

		const x = this.options.imageWidth + gridColWidth(0.5);
		const y = presentationDimensions.h - elementSizes.footnoteHeight;
		const h = elementSizes.footnoteHeight;
		const w =
			presentationDimensions.w - this.options.imageWidth - defaultViewPortPadding.horizontal - gridColWidth(0.5);

		slide.addText(this.slideData.footnote, {
			...copySmall,
			x,
			y,
			w,
			h,
			align: 'left',
			valign: 'middle',
			margin: 0,
			color: colors.blue
		});
	};
}
